import { useEffect, useState } from 'react'
import { CheckCircle2, Eye, EyeOff, Loader2, ShieldAlert } from 'lucide-react'
import { getAuthSupabase, isSupabaseConfigured } from '../lib/supabase'

/**
 * Password-recovery landing page, served at /reset-password (alias
 * /auth/reset). The BimaUncle app calls
 * supabase.auth.resetPasswordForEmail(email, { redirectTo:
 * 'https://bimauncle.com/reset-password' }); Supabase emails a link back
 * here carrying a one-time recovery credential. Because the reset is
 * requested on the device but the email may be opened anywhere, this page
 * is intentionally cross-device safe — it does NOT rely on a PKCE verifier
 * (which only exists on the requesting device).
 *
 * Supabase can deliver the credential three ways depending on the project's
 * email-template / flow config; we handle all of them:
 *   1. ?token_hash=…&type=recovery  → verifyOtp (recommended, cross-device)
 *   2. #access_token=…&type=recovery → detectSessionInUrl auto-sets session
 *   3. ?code=…                       → exchangeCodeForSession (same-device)
 * …plus the error case (?error=… / #error=…) when a link is expired/used.
 *
 * Supabase config the migration requires (done on the Supabase side):
 *   • Auth → URL Configuration → Redirect URLs must allow
 *     https://bimauncle.com/reset-password
 *   • The "Reset Password" email template's link points at that URL.
 */

type Phase = 'verifying' | 'ready' | 'saving' | 'done' | 'error'

function readParams(): URLSearchParams {
  // Merge query string and hash fragment — Supabase uses either depending
  // on flow. Hash form is "#access_token=…&type=recovery".
  const merged = new URLSearchParams(window.location.search)
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash
  for (const [k, v] of new URLSearchParams(hash)) merged.set(k, v)
  return merged
}

export function ResetPassword() {
  const [phase, setPhase] = useState<Phase>('verifying')
  const [error, setError] = useState<string>('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)

  // Establish the recovery session from whatever the link carried.
  useEffect(() => {
    let cancelled = false

    async function establish() {
      if (!isSupabaseConfigured()) {
        if (!cancelled) {
          setPhase('error')
          setError(
            'Password reset is temporarily unavailable. Please email hello@bimauncle.com.',
          )
        }
        return
      }
      const sb = getAuthSupabase()!
      const params = readParams()

      const linkError =
        params.get('error_description') || params.get('error')
      if (linkError) {
        if (!cancelled) {
          setPhase('error')
          setError(decodeURIComponent(linkError.replace(/\+/g, ' ')))
        }
        return
      }

      try {
        const tokenHash = params.get('token_hash') || params.get('token')
        const type = params.get('type')
        const code = params.get('code')

        if (tokenHash && (type === 'recovery' || !type)) {
          const { error } = await sb.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          })
          if (error) throw error
        } else if (code) {
          const { error } = await sb.auth.exchangeCodeForSession(code)
          if (error) throw error
        }
        // Implicit (#access_token) flow: detectSessionInUrl already parsed
        // it on client init. Confirm a session now exists either way.
        const { data } = await sb.auth.getSession()
        if (!data.session) {
          throw new Error(
            'This reset link is invalid or has expired. Please request a new one from the app.',
          )
        }
        // Strip the credential from the address bar so a refresh / shared
        // URL can't replay it.
        window.history.replaceState(null, '', window.location.pathname)
        if (!cancelled) setPhase('ready')
      } catch (e) {
        if (!cancelled) {
          setPhase('error')
          setError(
            e instanceof Error
              ? e.message
              : 'This reset link is invalid or has expired. Please request a new one from the app.',
          )
        }
      }
    }

    void establish()
    return () => {
      cancelled = true
    }
  }, [])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setPhase('saving')
    const sb = getAuthSupabase()!
    const { error } = await sb.auth.updateUser({ password })
    if (error) {
      setPhase('ready')
      setError(error.message || 'Could not update password — please try again.')
      return
    }
    await sb.auth.signOut()
    setPhase('done')
  }

  return (
    <div className="min-h-screen bg-[var(--color-canvas,#f7f8fa)]">
      <main className="mx-auto max-w-md px-5 py-12 sm:px-6">
        {/* App logo, centered — matches the in-app brand pattern */}
        <a
          href="/"
          aria-label="BimaUncle home"
          className="mb-8 flex flex-col items-center"
        >
          <img src="/logo.png" alt="BimaUncle" className="h-20 w-auto" />
        </a>
        {phase === 'verifying' && (
          <div className="card text-center">
            <Loader2 size={28} className="mx-auto animate-spin text-brand" />
            <p className="mt-4 text-ink-soft">Checking your reset link…</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="card text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-status-errorDark">
              <ShieldAlert size={26} />
            </div>
            <h1 className="mt-4 text-xl font-bold">Reset link problem</h1>
            <p className="mt-2 text-sm text-ink-soft">{error}</p>
            <p className="mt-4 text-sm text-ink-soft">
              Open the BimaUncle app and tap “Forgot password” to get a fresh
              link, or email{' '}
              <a
                href="mailto:hello@bimauncle.com"
                className="font-semibold text-brand underline"
              >
                hello@bimauncle.com
              </a>
              .
            </p>
          </div>
        )}

        {phase === 'done' && (
          <div className="card text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-status-successDark">
              <CheckCircle2 size={26} />
            </div>
            <h1 className="mt-4 text-xl font-bold">Password updated</h1>
            <p className="mt-2 text-sm text-ink-soft">
              Your password has been changed. Open the BimaUncle app and sign in
              with your new password.
            </p>
          </div>
        )}

        {(phase === 'ready' || phase === 'saving') && (
          <form className="card" onSubmit={onSubmit} noValidate>
            <h1 className="text-xl font-bold tracking-tight">Set a new password</h1>
            <p className="mt-1.5 text-sm text-ink-soft">
              Choose a strong password you don’t use anywhere else.
            </p>

            {error && (
              <div className="mt-4 rounded-input border border-red-200 bg-red-50 p-3 text-sm text-status-errorDark">
                {error}
              </div>
            )}

            <label htmlFor="new-password" className="mt-5 block">
              <span className="text-sm font-semibold">New password</span>
              <div className="relative">
                <input
                  id="new-password"
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input mt-1.5 pr-11"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  aria-label={show ? 'Hide password' : 'Show password'}
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
                  style={{ marginTop: '0.1875rem' }}
                >
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            <label htmlFor="confirm-password" className="mt-4 block">
              <span className="text-sm font-semibold">Confirm password</span>
              <input
                id="confirm-password"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input mt-1.5"
                placeholder="Re-enter the password"
                required
                minLength={8}
              />
            </label>

            <button
              type="submit"
              disabled={phase === 'saving'}
              className="btn-primary mt-6 flex w-full items-center justify-center gap-2"
            >
              {phase === 'saving' ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Updating…
                </>
              ) : (
                'Update password'
              )}
            </button>
          </form>
        )}
      </main>
    </div>
  )
}
